import { TemplateRef } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * Declarative page-header action model used by AppShellComponent.
 *
 * Two flavors are supported:
 *  - `button`: a regular action button (auto-collapses to the overflow dropdown
 *    on mobile unless `pinned: true`).
 *  - `custom`: a TemplateRef that the page can render itself (e.g. file input,
 *    custom widget). Stays visible in the toolbar on all viewports unless the
 *    page opts in to mobile-collapsing via `moveToSubMenuOnMobile: true`.
 */
export type PageActionVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'neutral'
  | 'ghost';

export type PageActionTone = 'solid' | 'soft' | 'outline' | 'transparent';

/** Where the action is allowed to render. */
export type PageActionVisibility =
  /** Default: desktop = toolbar; mobile = overflow menu (unless pinned). */
  | 'auto'
  /** Always rendered (desktop toolbar + mobile toolbar). Same as `pinned: true`. */
  | 'always'
  /** Only rendered on desktop. Hidden on mobile (no overflow entry). */
  | 'desktop-only'
  /** Only rendered on mobile (toolbar or overflow). Hidden on desktop. */
  | 'mobile-only';

export interface PageActionButton {
  type: 'button';
  /** Stable identifier (used for trackBy). */
  id?: string;
  /** Visible label. Also used as aria-label for icon-only buttons. */
  label: string;
  icon?: IconDefinition;
  /** Color/intent. Default: 'secondary'. */
  variant?: PageActionVariant;
  /** Visual treatment. Defaults to 'solid' for primary/danger, 'soft' for others. */
  tone?: PageActionTone;
  /**
   * If true, the action is always rendered as a toolbar button on both
   * mobile and desktop (never collapsed into the overflow dropdown).
   */
  pinned?: boolean;
  /** Fine-grained visibility control. Overrides `pinned` when set. */
  visibility?: PageActionVisibility;
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
  /**
   * If true, this button is ALWAYS rendered inside the overflow `⋯` dropdown,
   * regardless of viewport size (mobile and desktop). Useful for rarely-used
   * destructive or secondary actions that should not clutter the toolbar.
   *
   * When true, `pinned` and `visibility` are ignored.
   */
  alwaysInSubMenu?: boolean;
  onClick: (event?: Event) => void;
}

export interface PageActionCustom {
  type: 'custom';
  id?: string;
  template: TemplateRef<unknown>;
  /**
   * If true, the custom item is rendered in a full-width second row on mobile
   * (analogous to `pinned` for buttons), instead of inline in the toolbar.
   * On desktop it stays inline. Default: false.
   */
  pinned?: boolean;
  /**
   * If true, this custom action is rendered inside the overflow dropdown on
   * mobile instead of the toolbar. Default: false (always visible in toolbar).
   * Ignored when `pinned` or `alwaysInSubMenu` is set.
   */
  moveToSubMenuOnMobile?: boolean;
  /**
   * If true, this custom action is ALWAYS rendered inside the overflow `⋯`
   * dropdown, regardless of viewport size. Takes precedence over `pinned`,
   * `moveToSubMenuOnMobile` and `visibility`.
   */
  alwaysInSubMenu?: boolean;
  /** Optional override for desktop-only / mobile-only rendering. */
  visibility?: PageActionVisibility;
}

export type PageAction = PageActionButton | PageActionCustom;

/** Type guard: button action. */
export function isButtonAction(a: PageAction): a is PageActionButton {
  return a.type === 'button';
}

/** Type guard: custom action. */
export function isCustomAction(a: PageAction): a is PageActionCustom {
  return a.type === 'custom';
}


